/* eslint-disable prettier/prettier */
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";

/**
 * Exporta datos a PDF con tabla
 * @param {string} title - Título del documento
 * @param {Array} columns - Array de columnas [{header: 'Nombre', dataKey: 'nombre'}]
 * @param {Array} data - Array de datos
 * @param {string} fileName - Nombre del archivo
 */
export const exportTableToPDF = (title, columns, data, fileName = "reporte.pdf") => {
  const doc = new jsPDF();

  // Configuración
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Logo/Header
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text(title, pageWidth / 2, 20, { align: "center" });

  // Fecha y hora
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const fechaHora = new Date().toLocaleString("es-ES");
  doc.text(`Generado: ${fechaHora}`, 14, 30);

  // Tabla
  doc.autoTable({
    startY: 40,
    head: [columns.map((col) => col.header)],
    body: data.map((row) => columns.map((col) => row[col.dataKey] || "")),
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [52, 152, 219],
      textColor: 255,
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    margin: { top: 40 },
    didDrawPage: (data) => {
      // Footer con número de página
      doc.setFontSize(8);
      doc.text(
        `Página ${doc.internal.getCurrentPageInfo().pageNumber}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" }
      );
    },
  });

  // Guardar
  doc.save(fileName);
};

/**
 * Exporta un dashboard completo a PDF (captura de pantalla)
 * @param {string} elementId - ID del elemento a capturar
 * @param {string} fileName - Nombre del archivo
 */
export const exportDashboardToPDF = async (elementId, fileName = "dashboard.pdf") => {
  console.log("📄 exportDashboardToPDF - Iniciando con elementId:", elementId);
  const element = document.getElementById(elementId);

  if (!element) {
    console.error(`❌ Elemento con ID "${elementId}" no encontrado`);
    alert(`Error: No se encontró el elemento "${elementId}"`);
    return;
  }

  console.log("✅ Elemento encontrado, capturando...");

  try {
    // Capturar elemento
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
    });

    console.log("✅ Canvas creado, dimensiones:", canvas.width, "x", canvas.height);

    const imgData = canvas.toDataURL("image/png");

    // Calcular orientación y tamaño
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const orientation = imgWidth > imgHeight ? "landscape" : "portrait";

    console.log("📐 Orientación:", orientation);

    const pdf = new jsPDF({
      orientation: orientation,
      unit: "mm",
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Calcular dimensiones manteniendo aspect ratio
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const finalWidth = imgWidth * ratio;
    const finalHeight = imgHeight * ratio;

    // Centrar imagen
    const x = (pdfWidth - finalWidth) / 2;
    const y = (pdfHeight - finalHeight) / 2;

    pdf.addImage(imgData, "PNG", x, y, finalWidth, finalHeight);
    console.log("💾 Guardando PDF:", fileName);
    pdf.save(fileName);
    console.log("✅ PDF guardado exitosamente");
  } catch (error) {
    console.error("❌ Error al exportar PDF:", error);
    alert("Error al generar el PDF. Por favor intente nuevamente.");
  }
};

/**
 * Exporta datos a Excel
 * @param {Array} data - Array de objetos con los datos
 * @param {string} sheetName - Nombre de la hoja
 * @param {string} fileName - Nombre del archivo
 */
export const exportToExcel = (data, sheetName = "Datos", fileName = "reporte.xlsx") => {
  console.log("📊 exportToExcel - Iniciando con", data.length, "registros");

  if (!data || data.length === 0) {
    console.warn("⚠️ No hay datos para exportar");
    alert("No hay datos para exportar");
    return;
  }

  try {
    console.log("✅ Creando worksheet...");
    // Crear worksheet
    const ws = XLSX.utils.json_to_sheet(data);

    // Ajustar ancho de columnas
    const colWidths = [];
    const keys = Object.keys(data[0] || {});

    keys.forEach((key) => {
      const maxLength = Math.max(key.length, ...data.map((row) => String(row[key] || "").length));
      colWidths.push({ wch: Math.min(maxLength + 2, 50) });
    });

    ws["!cols"] = colWidths;

    console.log("✅ Creando workbook...");
    // Crear workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Exportar
    console.log("💾 Guardando Excel:", fileName);
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, fileName);
    console.log("✅ Excel guardado exitosamente");
  } catch (error) {
    console.error("❌ Error al exportar Excel:", error);
    alert("Error al generar el archivo Excel. Por favor intente nuevamente.");
  }
};

/**
 * Exporta múltiples hojas a Excel
 * @param {Array} sheets - Array de {name: string, data: Array}
 * @param {string} fileName - Nombre del archivo
 */
export const exportMultipleSheetsToExcel = (sheets, fileName = "reporte.xlsx") => {
  if (!sheets || sheets.length === 0) {
    alert("No hay datos para exportar");
    return;
  }

  try {
    const wb = XLSX.utils.book_new();

    sheets.forEach((sheet) => {
      if (sheet.data && sheet.data.length > 0) {
        const ws = XLSX.utils.json_to_sheet(sheet.data);

        // Ajustar ancho de columnas
        const keys = Object.keys(sheet.data[0] || {});
        const colWidths = keys.map((key) => {
          const maxLength = Math.max(
            key.length,
            ...sheet.data.map((row) => String(row[key] || "").length)
          );
          return { wch: Math.min(maxLength + 2, 50) };
        });
        ws["!cols"] = colWidths;

        XLSX.utils.book_append_sheet(wb, ws, sheet.name || "Sheet");
      }
    });

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, fileName);
  } catch (error) {
    console.error("Error al exportar Excel:", error);
    alert("Error al generar el archivo Excel. Por favor intente nuevamente.");
  }
};

/**
 * Prepara la página para impresión
 * @param {Function} callback - Función a ejecutar antes de imprimir
 */
export const handlePrint = (callback = null) => {
  if (callback) callback();

  setTimeout(() => {
    window.print();
  }, 100);
};

/**
 * Formatea número como moneda
 * @param {number} value - Valor a formatear
 * @param {string} currency - Código de moneda (default: MXN)
 * @returns {string} - Valor formateado
 */
export const formatCurrency = (value, currency = "MXN") => {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: currency,
  }).format(value || 0);
};

/**
 * Formatea fecha
 * @param {string|Date} date - Fecha a formatear
 * @returns {string} - Fecha formateada
 */
export const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("es-ES");
};

/**
 * Formatea número con separadores de miles
 * @param {number} value - Número a formatear
 * @param {number} decimals - Cantidad de decimales (default: 2)
 * @returns {string} - Número formateado
 */
export const formatNumber = (value, decimals = 2) => {
  return new Intl.NumberFormat("es-MX", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value || 0);
};

/**
 * Formatea porcentaje
 * @param {number} value - Valor a formatear
 * @param {number} decimals - Cantidad de decimales (default: 2)
 * @returns {string} - Porcentaje formateado
 */
export const formatPercentage = (value, decimals = 2) => {
  return `${formatNumber(value, decimals)}%`;
};
